import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth.js";
import { GoogleButton } from "../components/auth/GoogleButton.js";
import { colors } from "../components/ui/index.js";
import { useEffect, useState } from "react";
function renderError(code, message, attemptedEmail, retry) {
    switch (code) {
        case "not_authorised":
            return {
                title: "Your account isn't linked yet",
                tone: "warning",
                body: (_jsxs(_Fragment, { children: [_jsx("p", { style: { margin: "0 0 8px 0" }, children: attemptedEmail ? (_jsxs(_Fragment, { children: ["We don't have a tenant for ", _jsx("strong", { children: attemptedEmail }), "."] })) : (_jsx(_Fragment, { children: "We don't have a tenant for that Google account." })) }), _jsx("p", { style: { margin: 0 }, children: "Ask your admin to invite you, then sign in again." })] })),
                cta: { label: "Try a different account", onClick: retry },
            };
        case "account_suspended":
            return {
                title: "Your account is suspended",
                tone: "error",
                body: (_jsx("p", { style: { margin: 0 }, children: "Sign-in is paused on this account. Contact your admin to re-enable access \u2014 they can do this from the Booking Bot admin panel." })),
            };
        case "bad_request":
            return {
                title: "Sign-in didn't go through",
                tone: "error",
                body: (_jsx("p", { style: { margin: 0 }, children: "Google didn't return a usable token. Try again, or use a different browser if it keeps happening." })),
                cta: { label: "Try again", onClick: retry },
            };
        case "network_error":
            return {
                title: "Couldn't reach the server",
                tone: "error",
                body: (_jsx("p", { style: { margin: 0 }, children: "Check your internet connection and try again. If the problem persists, the booking bot may be down \u2014 please notify your admin." })),
                cta: { label: "Retry", onClick: retry },
            };
        case "unauthenticated":
            return {
                title: "Your session expired",
                tone: "warning",
                body: (_jsx("p", { style: { margin: 0 }, children: "Sign in again to continue." })),
            };
        default:
            return {
                title: "Sign-in failed",
                tone: "error",
                body: _jsx("p", { style: { margin: 0 }, children: message }),
                cta: { label: "Try again", onClick: retry },
            };
    }
}
function decodeEmailFromIdToken(idToken) {
    try {
        const payload = idToken.split(".")[1];
        if (!payload)
            return null;
        const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
        return typeof json.email === "string" ? json.email : null;
    }
    catch {
        return null;
    }
}
export function LoginPage() {
    const { user, signInWithGoogle, error, clearError } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    // Where to send the user after sign-in: the originally-requested path if they
    // were redirected here by RequireAuth, otherwise the dashboard.
    const fromState = location.state;
    const redirectTo = fromState?.from?.pathname ?? "/";
    useEffect(() => {
        if (user)
            nav(redirectTo, { replace: true });
    }, [user, nav, redirectTo]);
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
    // Track the email of the last attempted sign-in so we can echo it back in
    // "not_authorised" / "account_suspended" messages — much friendlier than
    // a generic "ask your admin" with no context about what to ask for.
    const [lastAttemptedEmail, setLastAttemptedEmail] = useState(null);
    const handleCredential = async (idToken) => {
        setLastAttemptedEmail(decodeEmailFromIdToken(idToken));
        await signInWithGoogle(idToken);
    };
    const errRender = error
        ? renderError(error.code, error.message, lastAttemptedEmail, clearError)
        : null;
    return (_jsx("div", { style: {
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            padding: 24,
            background: colors.bg,
        }, children: _jsxs("div", { style: {
                maxWidth: 400,
                width: "100%",
                background: colors.surface,
                borderRadius: 12,
                padding: 32,
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
            }, children: [_jsx("h1", { style: {
                        fontSize: 22,
                        fontWeight: 700,
                        margin: "0 0 6px 0",
                        color: colors.text,
                    }, children: "Booking Bot" }), _jsx("p", { style: {
                        color: colors.textSecondary,
                        fontSize: "0.9rem",
                        margin: "0 0 24px 0",
                    }, children: "Sign in with the Google account configured by your admin." }), clientId ? (_jsx(GoogleButton, { clientId: clientId, onCredential: handleCredential })) : (_jsxs("div", { style: configErrorStyle, children: [_jsx("strong", { children: "Sign-in not available." }), _jsxs("p", { style: { margin: "4px 0 0 0", fontSize: "0.82rem" }, children: [_jsx("code", { children: "VITE_GOOGLE_CLIENT_ID" }), " isn't configured. Tell your admin \u2014 the deploy needs the Google client ID set before tenants can sign in."] })] })), errRender && (_jsxs("div", { role: "alert", style: {
                        marginTop: 20,
                        padding: 14,
                        borderRadius: 8,
                        border: `1px solid ${errRender.tone === "error" ? colors.dangerBorder : colors.warningBorder}`,
                        background: errRender.tone === "error" ? colors.dangerLight : colors.warningLight,
                        color: errRender.tone === "error" ? colors.dangerDark : colors.warningDark,
                        fontSize: "0.86rem",
                        lineHeight: 1.45,
                    }, children: [_jsx("strong", { style: { display: "block", marginBottom: 6 }, children: errRender.title }), errRender.body, errRender.cta && (_jsx("button", { type: "button", onClick: errRender.cta.onClick, style: {
                                marginTop: 10,
                                background: "transparent",
                                border: `1px solid ${errRender.tone === "error" ? colors.dangerDark : colors.warningDark}`,
                                color: errRender.tone === "error" ? colors.dangerDark : colors.warningDark,
                                padding: "6px 12px",
                                borderRadius: 6,
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                cursor: "pointer",
                            }, children: errRender.cta.label }))] })), _jsx("p", { style: {
                        marginTop: 24,
                        fontSize: "0.75rem",
                        color: colors.textTertiary,
                        textAlign: "center",
                    }, children: "By signing in you accept that the bot will book courts on your behalf using the credentials you provide." })] }) }));
}
const configErrorStyle = {
    padding: 14,
    borderRadius: 8,
    border: `1px solid ${colors.dangerBorder}`,
    background: colors.dangerLight,
    color: colors.dangerDark,
    fontSize: "0.88rem",
};
